import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FileSpreadsheet, FileText, MoreHorizontal } from 'lucide-react';
import { PostureChip } from '@/components/ui/posture-chip';
import { Badge } from '@/components/ui/badge';
import { ExcelWorkbookModal } from '@/components/modals/excel-workbook-modal';
import { cn } from '@/lib/utils/cn';
import { formatDateTime, titleCase } from '@/lib/utils/format';
import { documentClassLabel } from '@/lib/document-classes';
import {
  isXlsxClassification,
  sharePointSourceOf,
  type ClassificationItem,
  type DocumentListItem,
} from '@/lib/api/documents';
import { DocumentIssue, documentHasIssue } from './document-issue';
import { PartyChip } from './party-chip';
import { PartyHint, hasAveroHint } from './party-change';
import { SharePointChip } from './sharepoint-chip';

/**
 * T-0099 / E10 — Document card row.
 *
 * Visual contract: `design_handoff_caliber_v2/prototype/screens-engagement.js`
 * → `docRow()` helper at L367.
 *
 * Single row in the right-pane document grid. Each row shows:
 *   - file icon + filename (truncated)
 *   - classification badge (the predicted/corrected type)
 *   - ingestion-status chip — the document's `process_status` until it is
 *     indexed (T-0160), then the classification status with the
 *     ingestion-status synonym mapping per PR #92. The vocab is: uploading /
 *     parsing / indexing / indexed / classified / failed.
 *   - for a failed or unfinished document, the reason and Retry / Delete
 *     under the row (`DocumentIssue`, T-0160)
 *   - the party chip — Vendor / Avero / Client, whose document it is (E19 /
 *     T-0215) — and, under the row, the hint when Avero is named on page 1
 *     of a vendor-tagged document, with the one-click change for leads
 *   - the `SharePoint` provenance chip when the connected folder took the
 *     document (T-0211), linking to the file in SharePoint; nothing for an
 *     upload
 *   - upload timestamp (mono small)
 *   - "open" affordance — entire row links to the document detail route
 *
 * Clicking the row navigates to the T-0036 single-document detail route
 * (`/engagements/:id/documents/:doc_id`). The Excel mime-type rendering
 * happens inside that route; this component just wires the link.
 *
 * Tokens-only — no inline hex. Posture/AI tokens reused from the design
 * system primitives in `components/ui/`.
 */

export interface DocCardProps {
  engagementId: string;
  item: ClassificationItem;
  /**
   * T-0160 — the document's row from the documents list. Its
   * `process_status` drives the chip until the document is indexed
   * (uploading / parsing / indexing / failed), and a failed or unfinished
   * document gets its reason and Retry / Delete under the row.
   */
  document?: DocumentListItem | null;
  /** Number of pending AI proposals attached to this doc, if any. */
  proposalCount?: number;
  className?: string;
}

/**
 * Map the classification `status` string to the ingestion-status vocab.
 * The backend classification status is `pending | predicted | confirmed |
 * corrected | failed`; the user-facing ingestion vocab is `uploading |
 * parsed | indexed | classified | failed`. The map keeps the UI label
 * consistent with the prototype while still respecting the wire shape.
 */
function ingestionStatus(
  item: ClassificationItem,
  document?: DocumentListItem | null,
): {
  label: string;
  posture: 'healthy' | 'at-risk' | 'critical' | 'unassessed';
} {
  // T-0160 — the pipeline status wins until the document is indexed: a
  // document that is still uploading, being read, or whose read failed has
  // no meaningful classification status yet.
  switch (document?.process_status) {
    case 'uploading':
      return { label: 'Uploading…', posture: 'unassessed' };
    case 'pending':
      return { label: 'Parsing…', posture: 'unassessed' };
    case 'extracted':
      return { label: 'Indexing…', posture: 'unassessed' };
    case 'failed':
      return { label: 'Failed', posture: 'critical' };
    case 'indexed':
      return item.status === 'confirmed' || item.status === 'corrected'
        ? { label: 'Classified', posture: 'healthy' }
        : { label: 'Indexed', posture: 'healthy' };
    default:
      break;
  }
  switch (item.status) {
    case 'confirmed':
    case 'corrected':
      return { label: 'Classified', posture: 'healthy' };
    case 'predicted':
      return { label: 'Indexed', posture: 'healthy' };
    case 'failed':
      return { label: 'Failed', posture: 'critical' };
    case 'pending':
      return { label: 'Parsing…', posture: 'unassessed' };
    default:
      return { label: titleCase(item.status), posture: 'unassessed' };
  }
}

/**
 * Render the human-readable classification type. The backend exposes both a
 * predicted and a corrected type; the corrected one wins when present
 * (consultant edited the AI's guess). "Not classified" until one exists.
 */
function classificationLabel(item: ClassificationItem): string {
  return documentClassLabel(item.corrected_type ?? item.predicted_type);
}

export function DocCard({
  engagementId,
  item,
  document,
  proposalCount = 0,
  className,
}: DocCardProps) {
  const ingestion = ingestionStatus(item, document);
  const classification = classificationLabel(item);
  const uploadedAt = document?.created_at ?? item.classified_at ?? null;
  // T-0120: classify by the cached `mime_type` (with DocumentVersion
  // fallback for legacy rows) instead of title-suffix sniffing. The
  // backend now ships the mime on the classification list so we don't
  // mis-detect "summary.xlsx (review).pdf" as an xlsx.
  const isXlsx = isXlsxClassification(item);
  const [xlsxModalOpen, setXlsxModalOpen] = useState(false);

  return (
    <li className={cn('list-none border-b border-ink-100', className)}>
      <div
        className={cn(
          'flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors',
          'text-ink-900 hover:bg-paper-50',
        )}
      >
        <Link
          to="/engagements/$engagementId/documents/$documentId"
          params={{ engagementId, documentId: item.document_id }}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-3 no-underline',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600 focus-visible:rounded-card',
          )}
        >
          <div
            className="flex h-9 w-9 flex-none items-center justify-center rounded-card border border-ink-200 bg-paper-50 text-meta font-semibold uppercase text-ink-500"
            aria-hidden="true"
          >
            {isXlsx ? <FileSpreadsheet className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="truncate text-body-sm font-medium text-ink-900"
              title={item.document_title}
            >
              {item.document_title}
            </div>
            <div className="mt-0.5 truncate text-meta text-ink-500">
              {classification}
              {uploadedAt ? <> · uploaded {formatDateTime(uploadedAt)}</> : null}
            </div>
          </div>
        </Link>
        <PostureChip status={ingestion.posture}>{ingestion.label}</PostureChip>
        {document ? <PartyChip party={document.source} /> : null}
        <SharePointChip source={sharePointSourceOf(document)} />
        {proposalCount > 0 ? (
          <Badge variant="outline" className="border-ai-surface-border bg-ai-surface text-ai-ink">
            {proposalCount} proposals
          </Badge>
        ) : null}
        {isXlsx ? (
          <button
            type="button"
            data-open-xlsx={item.document_id}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setXlsxModalOpen(true);
            }}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 rounded-card border border-ink-200 bg-paper-0 px-2 text-meta font-medium text-ink-700',
              'hover:bg-paper-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
            )}
          >
            <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Open inline</span>
          </button>
        ) : null}
        <span
          className="flex h-7 w-7 flex-none items-center justify-center rounded-card text-ink-500 hover:bg-paper-100"
          aria-hidden="true"
        >
          <MoreHorizontal className="h-4 w-4" />
        </span>
      </div>
      {document && documentHasIssue(document) ? (
        // Aligned with the title: px-5 (20px) + 36px icon + 12px gap.
        <div className="-mt-1.5 pb-3 pl-[68px] pr-5">
          <DocumentIssue engagementId={engagementId} document={document} />
        </div>
      ) : null}
      {document && hasAveroHint(document.source, document.party_hint) ? (
        <div className="-mt-1.5 pb-3 pl-[68px] pr-5">
          <PartyHint
            engagementId={engagementId}
            documentId={item.document_id}
            party={document.source}
            partyHint={document.party_hint}
          />
        </div>
      ) : null}
      {isXlsx ? (
        <ExcelWorkbookModal
          engagementId={engagementId}
          documentId={item.document_id}
          open={xlsxModalOpen}
          onOpenChange={setXlsxModalOpen}
        />
      ) : null}
    </li>
  );
}
